import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    House, ChartPieSlice, Wallet, Receipt, TrendUp, Buildings, Bank, Sparkle,
    Files, SignOut, User as UserIcon, ChartBar, Sliders, FileText,
    ChatCircle, UsersThree, Coins, List, X
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { isDemoMode } from "../lib/demoStore";

const nav = [
    { to: "/", label: "Snapshot", icon: House, tid: "nav-snapshot" },
    { to: "/balance-sheet", label: "Balance Sheet", icon: Wallet, tid: "nav-balance-sheet" },
    { to: "/transactions", label: "Transactions", icon: Receipt, tid: "nav-transactions" },
    { to: "/cash-flow", label: "Cash Flow", icon: ChartBar, tid: "nav-cashflow" },
    { to: "/money-flow", label: "Money Movie", icon: ChartBar, tid: "nav-money-flow" },
    { to: "/lifestyle", label: "Lifestyle", icon: ChartPieSlice, tid: "nav-lifestyle" },
    { to: "/investment-committee", label: "Investment Committee", icon: ChartBar, tid: "nav-ic" },
    { to: "/prices", label: "Live Prices", icon: TrendUp, tid: "nav-prices" },
    { to: "/pendaz", label: "Pendaz Business", icon: Buildings, tid: "nav-pendaz", hideInDemo: true },
    { to: "/smsf", label: "SMSF", icon: Bank, tid: "nav-smsf" },
    { to: "/retirement", label: "Retirement Planner", icon: TrendUp, tid: "nav-retirement" },
    { to: "/property", label: "Property Modeller", icon: House, tid: "nav-property" },
    { to: "/registers", label: "Registers", icon: Files, tid: "nav-registers" },
    { to: "/assumptions", label: "Assumptions", icon: Sliders, tid: "nav-assumptions" },
    { to: "/reports", label: "Reports", icon: FileText, tid: "nav-reports" },
    { to: "/tax", label: "Tax", icon: Coins, tid: "nav-tax" },
    { to: "/tax-pack", label: "Tax Pack", icon: FileText, tid: "nav-tax-pack" },
    { to: "/copilot", label: "AI Copilot", icon: ChatCircle, tid: "nav-copilot" },
    { to: "/insights", label: "AI Insights", icon: Sparkle, tid: "nav-insights" },
    { to: "/users", label: "Users", icon: UsersThree, tid: "nav-users", ownerOnly: true },
];

function SidebarInner({ user, onLogout, onLinkClick }) {
    return (
        <>
            <div className="px-7 py-6">
                <div className="label-eyebrow mb-2">{isDemoMode() ? "Family" : "Andrews"}</div>
                <h1 className="font-serif text-2xl md:text-3xl leading-none tracking-tight text-[var(--ink)]">
                    Financial Planner
                </h1>
                <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[var(--muted)]">
                    {isDemoMode() ? "Demo Sandbox" : "2026 Edition"}
                </div>
            </div>
            <nav className="px-3 pb-6 flex-1">
                {nav.filter(n => (!n.ownerOnly || user?.role === "owner") && !(n.hideInDemo && isDemoMode())).map((n) => {
                    const Icon = n.icon;
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={n.tid}
                            onClick={onLinkClick}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded text-sm transition-colors ${
                                    isActive
                                        ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                        : "text-[var(--ink)] hover:bg-black/5"
                                }`
                            }
                        >
                            <Icon size={18} weight="regular" />
                            <span>{n.label}</span>
                        </NavLink>
                    );
                })}
            </nav>
            <div className="px-6 py-5 border-t border-[var(--line)]">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--eucalyptus)] flex items-center justify-center text-[var(--bone)] shrink-0">
                        <UserIcon size={16} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[var(--muted)]" data-testid="current-user-role">
                            {isDemoMode()
                                ? "Demo household"
                                : user?.role === "owner" ? "Darren & Penelope"
                                : user?.role === "spouse" ? "Darren & Penelope"
                                : user?.role || ""}
                        </div>
                    </div>
                </div>
                <button
                    onClick={onLogout}
                    className="btn-ghost w-full flex items-center justify-center gap-2 text-sm"
                    data-testid="logout-btn"
                >
                    <SignOut size={14} /> Sign out
                </button>
            </div>
        </>
    );
}

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleLogout = async () => { await logout(); navigate("/login"); };
    const currentLabel = nav.find(n => n.to === loc.pathname)?.label
        || (loc.pathname.startsWith("/") ? "" : "");

    return (
        <div className="min-h-screen md:flex" style={{ background: "var(--bone)" }}>
            {/* Desktop sidebar (md+) */}
            <aside className="hidden md:flex md:flex-col w-72 shrink-0 hairline-b bg-white/60 backdrop-blur-xl border-r border-[var(--line)] sticky top-0 h-screen overflow-y-auto"
                   data-testid="app-sidebar">
                <SidebarInner user={user} onLogout={handleLogout} />
            </aside>

            {/* Mobile top bar */}
            <header className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[var(--line)] flex items-center justify-between px-4 py-3"
                    data-testid="mobile-topbar">
                <button onClick={() => setDrawerOpen(true)}
                        className="p-2 -ml-2 rounded hover:bg-black/5"
                        data-testid="mobile-menu-btn"
                        aria-label="Open menu">
                    <List size={22} />
                </button>
                <div className="font-serif text-lg tracking-tight truncate text-center flex-1 px-2"
                     data-testid="mobile-page-title">
                    {currentLabel || (isDemoMode() ? "Financial Planner" : "Andrews")}
                </div>
                <div className="w-8 h-8 rounded-full bg-[var(--eucalyptus)] flex items-center justify-center text-[var(--bone)]">
                    <UserIcon size={14} />
                </div>
            </header>

            {/* Mobile drawer */}
            {drawerOpen && (
                <>
                    <div className="fixed inset-0 z-40 bg-black/40 md:hidden"
                         onClick={() => setDrawerOpen(false)}
                         data-testid="mobile-drawer-backdrop" />
                    <aside className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl overflow-y-auto flex flex-col md:hidden"
                           data-testid="mobile-drawer">
                        <button onClick={() => setDrawerOpen(false)}
                                className="absolute top-4 right-4 p-1 rounded hover:bg-black/5"
                                data-testid="mobile-drawer-close"
                                aria-label="Close menu">
                            <X size={20} />
                        </button>
                        <SidebarInner
                            user={user}
                            onLogout={handleLogout}
                            onLinkClick={() => setDrawerOpen(false)}
                        />
                    </aside>
                </>
            )}

            <main className="flex-1 min-w-0">
                <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1400px]">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
